import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, Lock, Play, Sparkles, Package, Gift, Clock, Tag, Star, PawPrint
} from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import AddressModal from '../../components/AddressModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_META = {
  "Phase 1: Foundation": { subtitle: "Understand Your Dog", color: "bg-edupaws-sage" },
  "Phase 2: Application": { subtitle: "Practice in Real Situations", color: "bg-edupaws-navy" },
  "Phase 3: Transformation": { subtitle: "Become the Leader", color: "bg-amber-500" },
};

export default function OwnerSeries() {
  const navigate = useNavigate();
  const { user, activeDog } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [videos, setVideos] = useState({});
  const [unlockedVideos, setUnlockedVideos] = useState([]);
  const [unlockedBoxes, setUnlockedBoxes] = useState([]);
  const [progress, setProgress] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user, activeDog]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const [boxesRes, unlockedRes, unlockedVidsRes] = await Promise.all([
        axios.get(`${API}/series`),
        axios.get(`${API}/unlocked/${user.id}`),
        axios.get(`${API}/unlocked-videos/${user.id}`)
      ]);
      
      const boxesData = boxesRes.data;
      setBoxes(boxesData);
      setUnlockedBoxes(unlockedRes.data);
      setUnlockedVideos(unlockedVidsRes.data);

      const videoMap = {};
      for (const box of boxesData) {
        const vidsRes = await axios.get(`${API}/videos/series/${box.id}`);
        videoMap[box.id] = vidsRes.data;
      }
      setVideos(videoMap);

      if (activeDog) {
        const progRes = await axios.get(`${API}/progress/${activeDog.id}`);
        setProgress(progRes.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isVideoUnlocked = (videoId, video) => {
    if (video?.is_free) return true;
    if (unlockedVideos.some(uv => uv.video_id === videoId)) return true;
    if (unlockedBoxes.includes(video?.series_id)) return true;
    return false;
  };

  const getVideoProgress = (videoId) => {
    return progress.find(p => p.video_id === videoId);
  };

  // Check if user has any paid training boxes unlocked (not just free ones)
  const isSubscribed = boxes.filter(b => !b.is_free).some(b => unlockedBoxes.includes(b.id));

  // Separate featured videos from training boxes
  const featuredBoxes = boxes.filter(b => b.phase === "Featured");
  const trainingBoxes = boxes.filter(b => b.phase !== "Featured");

  const completedBoxes = trainingBoxes.filter(box => {
    const boxVids = videos[box.id] || [];
    return boxVids.length > 0 && boxVids.every(v => getVideoProgress(v.id)?.status === 'complete');
  }).length;

  const overallPercent = trainingBoxes.length > 0 ? Math.round((completedBoxes / trainingBoxes.length) * 100) : 0;

  const handleSubscribe = async (shippingAddress, promoCode) => {
    if (boxes.length === 0) return;
    setPurchasing(true);
    try {
      // Find Box 1 (not the free Welcome/Bonus videos)
      const box1 = boxes.find(b => b.order_index === 1) || boxes.find(b => !b.is_free);
      if (!box1) {
        toast.error('No boxes available');
        return;
      }
      const res = await axios.post(`${API}/subscriptions/create`, {
        user_id: user.id,
        series_id: box1.id,
        origin_url: window.location.origin,
        shipping_address: shippingAddress,
        promo_code: promoCode
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setPurchasing(false);
      setShowAddressModal(false);
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    setRedeemingPromo(true);
    try {
      await axios.post(`${API}/promo/redeem`, {
        user_id: user.id,
        code: promoCode.trim()
      });
      toast.success('Promo code redeemed! All 10 boxes unlocked!');
      setPromoCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
    } finally {
      setRedeemingPromo(false);
    }
  };

  const phases = [
    { key: "Phase 1: Foundation", boxes: trainingBoxes.filter(b => b.phase === "Phase 1: Foundation") },
    { key: "Phase 2: Application", boxes: trainingBoxes.filter(b => b.phase === "Phase 2: Application") },
    { key: "Phase 3: Transformation", boxes: trainingBoxes.filter(b => b.phase === "Phase 3: Transformation") },
  ].filter(p => p.boxes.length > 0);

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Header */}
      <div className="bg-white rounded-3xl p-8 border-2 border-gray-100 mb-8">
        <h1 data-testid="training-title" className="font-heading text-4xl font-bold text-edupaws-navy mb-3">
          10-Month Transformation
        </h1>
        <p className="text-gray-500 font-body max-w-2xl mb-4">
          Your journey through 10 monthly training boxes across 3 phases. Subscribe for <strong>$75/mo</strong> to get monthly boxes, app access, trainer sessions, and community membership.
        </p>
        <div className="max-w-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500 font-body">{completedBoxes} of {trainingBoxes.length} boxes completed</span>
            <span className="text-sm font-heading font-bold text-edupaws-sage">{overallPercent}%</span>
          </div>
          <Progress value={overallPercent} className="h-3" />
        </div>
      </div>

      {/* Add a Dog prompt */}
      {!activeDog && (
        <div
          data-testid="add-dog-banner"
          className="mb-8 bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => navigate('/owner/dogs')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
              <PawPrint className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="font-heading font-bold text-amber-900">Add your dog to start.</p>
              <p className="text-sm text-amber-700 font-body">Create a dog profile to track progress and watch videos.</p>
            </div>
          </div>
          <button className="font-heading font-bold text-sm bg-amber-500 text-white px-5 py-2 rounded-full shadow hover:bg-amber-600 transition-colors flex-shrink-0">
            Add a Dog
          </button>
        </div>
      )}

      {/* Featured Videos, Welcome & Bonus */}
      {featuredBoxes.length > 0 && (
        <div className="mb-8" data-testid="featured-videos-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {featuredBoxes.map((box) => {
              const boxVids = videos[box.id] || [];
              const video = boxVids[0];
              return (
                <div
                  key={box.id}
                  data-testid={`featured-${box.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="relative bg-white rounded-2xl overflow-hidden border-2 border-edupaws-sage/30 shadow-sm hover:shadow-lg transition-all group"
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Thumbnail */}
                    <div className="sm:w-48 h-36 sm:h-auto relative overflow-hidden flex-shrink-0">
                      <img
                        src={box.image_url || 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400'}
                        alt={box.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                          <Play className="w-6 h-6 text-edupaws-navy ml-0.5" />
                        </div>
                      </div>
                    </div>
                    {/* Info */}
                    <div className="flex-1 p-5 flex flex-col justify-between">
                      <div>
                        <div className="inline-flex items-center gap-1.5 bg-edupaws-sage/10 text-edupaws-sage px-3 py-1 rounded-full text-xs font-body font-bold mb-2">
                          <Star className="w-3 h-3" />
                          Free
                        </div>
                        <h3 className="font-heading text-lg font-bold text-edupaws-navy mb-1">{box.title}</h3>
                        <p className="text-sm text-gray-500 font-body line-clamp-2">{box.description}</p>
                      </div>
                      {video && (
                        <Link
                          to={`/owner/video/${video.id}`}
                          data-testid={`watch-featured-${box.title.toLowerCase().replace(/\s+/g, '-')}`}
                          className="mt-3 inline-flex items-center gap-2 bg-edupaws-navy text-white py-2 px-5 rounded-xl font-heading font-bold text-sm
                            shadow-[0_3px_0_#3C4753] hover:shadow-[0_1px_0_#3C4753] hover:translate-y-[2px] transition-all w-fit"
                        >
                          <Play className="w-4 h-4" /> Watch Now
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Subscribe Banner */}
      {!isSubscribed && (
        <div className="mb-8 relative p-6 rounded-2xl bg-edupaws-navy overflow-hidden shadow-xl" data-testid="subscribe-banner">
          <div className="absolute top-0 right-0 w-64 h-64 bg-edupaws-sage/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full mb-3">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span className="text-white font-body text-sm font-medium">Everything Included</span>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                Subscribe to Edu Paws
              </h3>
              <p className="text-white/90 font-body max-w-md">
                Unlock one box per month. Each $75 payment unlocks your next box. 10 months to a complete transformation.
              </p>
              <div className="flex flex-wrap gap-4 mt-4 justify-center md:justify-start">
                {["1 box per month", "Physical training kit", "App access", "Community"].map(item => (
                  <div key={item} className="flex items-center gap-2 text-white text-sm font-body">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="font-heading text-4xl font-bold text-white">$75<span className="text-lg">/mo</span></div>
                <div className="text-white/70 font-body text-sm">Cancel anytime</div>
              </div>
              <button
                onClick={() => setShowAddressModal(true)}
                disabled={purchasing}
                data-testid="subscribe-btn"
                className="flex items-center gap-2 bg-edupaws-sage text-white px-8 py-3.5 rounded-xl font-heading font-bold text-lg
                  shadow-[0_4px_0_#6b8073] hover:shadow-[0_2px_0_#6b8073] hover:translate-y-[2px] transition-all disabled:opacity-50"
              >
                <Gift className="w-5 h-5" />
                Subscribe Now
              </button>
              {/* Promo Code */}
              <div className="flex items-center gap-2 mt-1">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Promo code"
                    data-testid="promo-code-input"
                    className="h-10 pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-lg font-body text-sm uppercase"
                    onKeyDown={(e) => e.key === 'Enter' && handleRedeemPromo()}
                  />
                </div>
                <button
                  onClick={handleRedeemPromo}
                  disabled={redeemingPromo}
                  data-testid="redeem-promo-btn"
                  className="h-10 px-4 bg-white/20 hover:bg-white/30 text-white rounded-lg font-body font-bold text-sm transition-colors disabled:opacity-50"
                >
                  {redeemingPromo ? '...' : 'Redeem'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phases */}
      {phases.map((phase) => {
        const meta = PHASE_META[phase.key] || {};
        return (
          <div key={phase.key} className="mb-10" data-testid={`phase-section-${phase.key}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-1.5 h-8 rounded-full ${meta.color || 'bg-gray-400'}`} />
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-800">{phase.key}</h2>
                <p className="text-gray-500 font-body text-sm">{meta.subtitle}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {phase.boxes.map((box) => {
                const boxVids = videos[box.id] || [];
                const video = boxVids[0];
                const unlocked = video ? isVideoUnlocked(video.id, video) : false;
                const prog = video ? getVideoProgress(video.id) : null;
                const isComplete = prog?.status === 'complete';
                const isReady = prog?.status === 'unlocked';

                return (
                  <div
                    key={box.id}
                    data-testid={`box-card-${box.order_index}`}
                    className={`bg-white rounded-2xl overflow-hidden border-2 transition-all hover:shadow-lg
                      ${isComplete ? 'border-edupaws-sage/50' : 'border-gray-100'}`}
                  >
                    {/* Box Header */}
                    <div className={`p-5 ${isComplete ? 'bg-edupaws-sage/5' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-body font-bold text-gray-400 uppercase tracking-wider">
                          Month {box.order_index}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {isComplete && <CheckCircle className="w-5 h-5 text-edupaws-sage" />}
                          {isReady && !isComplete && <Play className="w-5 h-5 text-amber-500" />}
                          {!isReady && !isComplete && <Lock className="w-5 h-5 text-gray-300" />}
                        </div>
                      </div>
                      <h3 className="font-heading text-lg font-bold text-edupaws-navy mb-1">
                        {box.title}
                      </h3>
                      <p className="text-sm text-gray-500 font-body line-clamp-2">
                        {box.description}
                      </p>
                      {video?.duration_minutes && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-400 font-body">
                          <Clock className="w-3 h-3" />
                          {video.duration_minutes} min
                        </div>
                      )}
                    </div>

                    {/* Box Contents */}
                    {video?.box_tool && (
                      <div className="px-5 py-3 bg-gray-50/80 border-t border-gray-100 space-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-edupaws-navy font-body font-medium">
                          <Package className="w-3.5 h-3.5 text-edupaws-sage" />
                          What's in the Box
                        </div>
                        {[video.box_tool, video.box_toy, video.box_swag].filter(Boolean).map((item, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500 font-body">
                            <div className="w-1 h-1 rounded-full bg-edupaws-sage flex-shrink-0" />
                            <span className="truncate">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Action */}
                    <div className="p-4 border-t border-gray-100">
                      {(isReady || isComplete) ? (
                        <Link
                          to={`/owner/video/${video?.id}`}
                          data-testid={`watch-video-${box.order_index}`}
                          className="w-full flex items-center justify-center gap-2 bg-edupaws-navy text-white py-2.5 rounded-xl font-heading font-bold text-sm
                            shadow-[0_3px_0_#3C4753] hover:shadow-[0_1px_0_#3C4753] hover:translate-y-[2px] transition-all"
                        >
                          <Play className="w-4 h-4" />
                          {isComplete ? 'Watch Again' : 'Watch Now'}
                        </Link>
                      ) : isSubscribed ? (
                        <div className="w-full flex items-center justify-center gap-2 bg-gray-200 text-gray-500 py-2.5 rounded-xl font-heading font-bold text-sm cursor-not-allowed">
                          <Lock className="w-4 h-4" />
                          Unlocks Next Month
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAddressModal(true)}
                          data-testid={`subscribe-${box.order_index}`}
                          className="w-full flex items-center justify-center gap-2 bg-edupaws-sage text-white py-2.5 rounded-xl font-heading font-bold text-sm
                            shadow-[0_3px_0_#6b8073] hover:shadow-[0_1px_0_#6b8073] hover:translate-y-[2px] transition-all"
                        >
                          <Sparkles className="w-4 h-4" />
                          Subscribe to Unlock
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <AddressModal
        open={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSubmit={handleSubscribe}
        loading={purchasing}
      />
    </OwnerLayout>
  );
}